import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './SettingsPage.css';

// Meta periodically emails asking us to delete app-scoped user ids (ASIDs) from our
// database. Paste the contents of the CSV they provide and this checks every table
// and column for them. Reports only — it never deletes anything.
//
// Superadmin only; the dropdown entry that opens this page is gated on role.
function DataDeletionPage({ org, onClose }) {
  const [ids, setIds] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  // The file is read in the browser and only its text is sent to the API — the file
  // itself is never uploaded or written to disk. That matters here: this is a deletion
  // request, so the last thing we want is to store a copy of it server-side.
  const loadFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('That file is larger than 2MB — is it the right one?');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setIds(e.target.result || '');
      setFileName(file.name);
      setResult(null);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read that file');
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    loadFile(e.dataTransfer.files?.[0]);
  };

  const runCheck = async () => {
    if (!ids.trim()) return;
    setChecking(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/check-identifiers`,
        { ids },
        { withCredentials: true }
      );
      setResult(res.data);
      if (res.data.clean) {
        toast.success('✅ No matches — nothing to delete');
      } else {
        toast('⚠️ Matches found — review the results', { icon: '🚨' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Check failed';
      toast.error(msg);
      setResult({ success: false, error: msg });
    } finally {
      setChecking(false);
    }
  };

  const copyRecord = () => {
    if (!result?.success) return;
    const record = [
      'Meta data deletion request — check record',
      `Checked at:     ${result.checked_at}`,
      `Checked by:     ${result.checked_by}`,
      `Identifiers:    ${result.ids_checked}`,
      `Values scanned: ${result.values_scanned}`,
      `Tables scanned: ${(result.tables_scanned || []).join(', ')}`,
      `Result:         ${result.summary}`
    ].join('\n');
    navigator.clipboard.writeText(record)
      .then(() => toast.success('Record copied to clipboard'))
      .catch(() => toast.error('Could not copy'));
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-header-left">
          <button className="settings-back-btn" onClick={onClose}>
            ← Back to Dashboard
          </button>
          <div className="settings-title-group">
            <h1>Data Deletion Requests</h1>
            <p className="settings-subtitle">{org ? org.name : 'ReportFlow'}</p>
          </div>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <div className="settings-section-header">
            <h2>Check Meta Identifiers</h2>
            <p>
              Meta sends these requests periodically. Download the identifier file from
              App Dashboard → App settings → Advanced → Download User Identifiers, then paste
              its contents below. ReportFlow never stores app-scoped user IDs, so this should
              come back clean — this gives you proof for your records.
            </p>
          </div>

          {/* Choose a file, or drag one onto the box below, or just paste */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.txt,text/csv,application/json,text/plain"
            style={{ display: 'none' }}
            onChange={(e) => {
              loadFile(e.target.files?.[0]);
              e.target.value = ''; // allow re-selecting the same file
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={checking}
              style={{
                padding: '9px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
                background: '#fff', cursor: checking ? 'not-allowed' : 'pointer', fontSize: '14px'
              }}
            >
              📄 Choose CSV file
            </button>
            {fileName ? (
              <span style={{ fontSize: '13px', color: '#15803d' }}>
                ✅ {fileName}
                <button
                  type="button"
                  onClick={() => { setIds(''); setFileName(''); setResult(null); }}
                  style={{
                    marginLeft: '10px', border: 'none', background: 'none',
                    color: '#6b7280', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline'
                  }}
                >
                  clear
                </button>
              </span>
            ) : (
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                or drag the file onto the box below, or paste its contents
              </span>
            )}
          </div>

          <textarea
            value={ids}
            onChange={(e) => { setIds(e.target.value); if (fileName) setFileName(''); }}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            disabled={checking}
            rows={6}
            placeholder={'Paste the CSV contents here, or drop the file on this box, e.g.\n1234567890123456\n9876543210987654'}
            style={{
              width: '100%', padding: '12px', borderRadius: '6px',
              border: dragging ? '2px dashed #EC6433' : '1px solid #d1d5db',
              background: dragging ? '#fff7ed' : '#fff',
              fontFamily: 'monospace', fontSize: '13px',
              boxSizing: 'border-box', resize: 'vertical'
            }}
          />

          <button
            type="button"
            onClick={runCheck}
            disabled={checking || !ids.trim()}
            style={{
              marginTop: '12px', padding: '12px 20px', borderRadius: '6px',
              border: 'none', background: checking || !ids.trim() ? '#d1d5db' : '#EC6433',
              color: '#fff', fontWeight: 700, fontSize: '14px',
              cursor: checking || !ids.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {checking ? '⏳ Scanning database...' : '🔎 Check for matches'}
          </button>

          {result && result.success && (
            <div
              style={{
                marginTop: '20px', padding: '18px', borderRadius: '8px',
                background: result.clean ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${result.clean ? '#86efac' : '#fca5a5'}`
              }}
            >
              <div style={{
                fontWeight: 700, fontSize: '15px',
                color: result.clean ? '#15803d' : '#b91c1c', marginBottom: '10px'
              }}>
                {result.clean ? '✅ ' : '🚨 '}{result.summary}
              </div>

              <div style={{ fontSize: '13px', color: '#555', lineHeight: 1.7 }}>
                <div>Checked at: {new Date(result.checked_at).toLocaleString()}</div>
                <div>Identifiers checked: {result.ids_checked}</div>
                <div>
                  Values scanned: {result.values_scanned} across{' '}
                  {(result.tables_scanned || []).length} tables
                </div>
              </div>

              {result.exact_matches?.length > 0 && (
                <div style={{ marginTop: '14px' }}>
                  <strong style={{ fontSize: '13px', color: '#b91c1c' }}>
                    Exact matches — these must be deleted:
                  </strong>
                  <ul style={{ fontSize: '13px', fontFamily: 'monospace', marginTop: '6px' }}>
                    {result.exact_matches.map((m, i) => (
                      <li key={i}>{m.table}.{m.column} (row {m.rowid}) = {m.value}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.embedded_matches?.length > 0 && (
                <div style={{ marginTop: '14px' }}>
                  <strong style={{ fontSize: '13px', color: '#b45309' }}>
                    Embedded matches — review manually, likely coincidental:
                  </strong>
                  <ul style={{ fontSize: '13px', fontFamily: 'monospace', marginTop: '6px' }}>
                    {result.embedded_matches.slice(0, 25).map((m, i) => (
                      <li key={i}>{m.table}.{m.column} (row {m.rowid}) contains {m.id}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                type="button"
                onClick={copyRecord}
                style={{
                  marginTop: '14px', padding: '8px 14px', borderRadius: '6px',
                  border: '1px solid #d1d5db', background: '#fff',
                  cursor: 'pointer', fontSize: '13px'
                }}
              >
                📋 Copy record for your files
              </button>

              {!result.clean && (
                <div style={{ marginTop: '12px', fontSize: '13px', color: '#b91c1c' }}>
                  This tool only reports. Remove the rows above deliberately — nothing was deleted.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DataDeletionPage;
